import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

interface StickyActionBarProps {
  vehicle: {
    year: number;
    make: string;
    model: string;
    price: number;
    status?: string;
  };
  onTestDrive?: () => void;
  onPreApproval?: () => void;
}

export default function StickyActionBar({ vehicle, onTestDrive, onPreApproval }: StickyActionBarProps) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsVisible(window.scrollY > 600);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const r = 5.9 / 100 / 12;
  const n = 60;
  const loan = Math.max(vehicle.price - Math.round(vehicle.price * 0.1), 0);
  const monthly = (loan * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);

  const isSold = vehicle.status === 'sold';
  const isPending = vehicle.status === 'pending';

  if (!isVisible) return null;

  return (
    <div
      className="fixed left-0 right-0 bg-white border-t-2 border-[#E8E2D6] shadow-2xl z-40 lg:hidden"
      style={{ bottom: 'calc(54px + env(safe-area-inset-bottom, 0px))' }}
      role="region"
      aria-label="Vehicle purchase actions"
    >
      <div className="px-3 pt-2 pb-3">
        {/* Price row — stacks gracefully at 320px */}
        <div className="flex flex-col mb-2 min-w-0">
          <p className="text-xs font-semibold text-[#1A2332] truncate w-full mb-0.5">
            {vehicle.year} {vehicle.make} {vehicle.model}
          </p>
          <div className="flex items-baseline gap-1.5">
            <span className="text-sm font-bold text-[#1A2332] whitespace-nowrap">
              ${vehicle.price.toLocaleString()}
            </span>
            <span className="text-[11px] text-[#92400e] font-semibold whitespace-nowrap">
              ~${Math.round(monthly).toLocaleString()}/mo
            </span>
          </div>
        </div>

        {(isSold || isPending) ? (
          <div className="flex items-center gap-2">
            <div className="flex-1 text-center py-2">
              <p className="text-sm font-bold text-red-700">
                {isSold ? '🚫 Sold — But We Can Find You One' : '⏳ Under Contract — Join the Waitlist'}
              </p>
            </div>
            <a
              href="tel:1-855-468-6348"
              className="w-11 h-11 min-h-[44px] flex items-center justify-center bg-[#E8B84B] hover:bg-[#d4a43a] text-[#1A2332] rounded-xl transition-colors cursor-pointer flex-shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1E3A5F] focus-visible:ring-offset-2"
              aria-label="Call Rockhound Auto at 1-855-468-6348"
            >
              <span className="text-base" aria-hidden="true"><i className="ri-phone-line"></i></span>
            </a>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <button
              onClick={onTestDrive}
              aria-label="Check availability for this vehicle"
              className="flex-1 min-w-0 min-h-[44px] py-2.5 bg-[#1A2332] hover:bg-[#2C3E55] text-white font-bold rounded-xl flex items-center justify-center gap-1.5 transition-colors whitespace-nowrap cursor-pointer text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1E3A5F] focus-visible:ring-offset-2"
            >
              <span className="text-sm flex-shrink-0" aria-hidden="true"><i className="ri-flag-line"></i></span>
              <span>I Want This Car</span>
            </button>

            <a
              href="tel:1-855-468-6348"
              className="w-11 h-11 min-h-[44px] flex items-center justify-center bg-[#E8B84B] hover:bg-[#d4a43a] text-[#1A2332] rounded-xl transition-colors cursor-pointer flex-shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1E3A5F] focus-visible:ring-offset-2"
              aria-label="Call Rockhound Auto at 1-855-468-6348"
            >
              <span className="text-base" aria-hidden="true"><i className="ri-phone-line"></i></span>
            </a>

            <Link
              to={`/apply-for-financing?vehicle=${encodeURIComponent(`${vehicle.year} ${vehicle.make} ${vehicle.model}`)}`}
              aria-label="Get financing approval for this vehicle"
              className="flex-1 min-w-0 min-h-[44px] py-2.5 bg-[#E8B84B] hover:bg-[#d4a43a] text-[#1A2332] font-bold rounded-xl flex items-center justify-center gap-1.5 transition-colors whitespace-nowrap cursor-pointer text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1E3A5F] focus-visible:ring-offset-2"
            >
              <span className="text-sm flex-shrink-0" aria-hidden="true"><i className="ri-bank-card-line"></i></span>
              <span>Get Approved</span>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
